import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PlayerState, Channel, QualityLevel } from '../../types';
import type { TextTrack } from 'video.js';

interface PlayerState {
  currentChannel: Channel | null;
  volume: number;
  quality: string;
  isFullscreen: boolean;
  isPiP: boolean;
  playbackSpeed: number;
  availableQualities: string[];
  isQualityChanging: boolean;
  subtitlesEnabled: boolean;
  currentSubtitle: string | null;
  availableSubtitles: TextTrack[];
  settingsOpen: boolean;
  selectedQuality: string;
  autoQuality: boolean;
  currentActiveQuality: string | null;
  isSeeking: boolean;
  seekStartTime: number | null;
  seekTargetTime: number | null;
}

const initialState: PlayerState = {
  currentChannel: null,
  volume: 1,
  quality: 'auto',
  isFullscreen: false,
  isPiP: false,
  playbackSpeed: 1,
  availableQualities: [],
  isQualityChanging: false,
  subtitlesEnabled: false,
  currentSubtitle: null,
  availableSubtitles: [],
  settingsOpen: false,
  selectedQuality: 'auto',
  autoQuality: true,
  currentActiveQuality: null,
  isSeeking: false,
  seekStartTime: null,
  seekTargetTime: null,
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    setCurrentChannel: (state, action: PayloadAction<Channel>) => {
      state.currentChannel = action.payload;
    },
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = action.payload;
    },
    setQuality: (state, action: PayloadAction<string>) => {
      state.quality = action.payload;
    },
    setAvailableQualities: (state, action: PayloadAction<string[]>) => {
      state.availableQualities = action.payload;
    },
    setQualityChanging: (state, action: PayloadAction<boolean>) => {
      state.isQualityChanging = action.payload;
    },
    setFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload;
    },
    setPiP: (state, action: PayloadAction<boolean>) => {
      state.isPiP = action.payload;
    },
    setPlaybackSpeed: (state, action: PayloadAction<number>) => {
      state.playbackSpeed = action.payload;
    },
    setSubtitlesEnabled: (state, action: PayloadAction<boolean>) => {
      state.subtitlesEnabled = action.payload;
    },
    setCurrentSubtitle: (state, action: PayloadAction<string | null>) => {
      state.currentSubtitle = action.payload;
    },
    setAvailableSubtitles: (state, action: PayloadAction<TextTrack[]>) => {
      state.availableSubtitles = action.payload;
    },
    setSettingsOpen: (state, action: PayloadAction<boolean>) => {
      state.settingsOpen = action.payload;
    },
    setSelectedQuality: (state, action: PayloadAction<string>) => {
      state.selectedQuality = action.payload;
    },
    setAutoQuality: (state, action: PayloadAction<boolean>) => {
      state.autoQuality = action.payload;
    },
    setCurrentActiveQuality: (state, action: PayloadAction<string>) => {
      state.currentActiveQuality = action.payload;
    },
    setSeekingState: (state, action: PayloadAction<{
      isSeeking: boolean;
      startTime?: number | null;
      targetTime?: number | null;
    }>) => {
      state.isSeeking = action.payload.isSeeking;
      state.seekStartTime = action.payload.startTime ?? null;
      state.seekTargetTime = action.payload.targetTime ?? null;
    },
  },
});

export const {
  setCurrentChannel,
  setVolume,
  setQuality,
  setAvailableQualities,
  setQualityChanging,
  setFullscreen,
  setPiP,
  setPlaybackSpeed,
  setSubtitlesEnabled,
  setCurrentSubtitle,
  setAvailableSubtitles,
  setSettingsOpen,
  setSelectedQuality,
  setAutoQuality,
  setCurrentActiveQuality,
  setSeekingState,
} = playerSlice.actions;

export default playerSlice.reducer;
